/**
 * NPAW CDN-balancer probing options that may be set in the remote `video.cdnBalancerOptions`.
 *
 * The balancer probes CDNs by re-requesting the last downloaded segment. After a player is torn
 * down that segment still carries its (now dead) bpk-token, so the probes get 403/417 from the CDN
 * for several seconds; mid-session probes to the alternative CDNs account for the 417s seen on
 * healthy sessions. `probeOnlyOnBanned: true` reduces this to probing banned CDNs only.
 */
export type CdnBalancerProbingOptions = {
    probeOnlyOnBanned?: boolean;
    noProbing?: boolean;
    probingPolicy?: 'GET' | 'HEAD' | 'GET_RETRY_HEAD';
    chunkExpirationProbingMs?: number;
    timeBetweenHeads?: number;
};

const PROBING_POLICIES = ['GET', 'HEAD', 'GET_RETRY_HEAD'];

/**
 * Picks the probing options from the remote balancer config. Only valid values that are set are
 * returned, so NPAW keeps its own defaults for anything the config leaves out.
 */
export const getCdnBalancerProbingOptions = (
    config: Record<string, unknown> | null | undefined
): CdnBalancerProbingOptions => {
    if (!config) return {};

    const options: CdnBalancerProbingOptions = {};
    if (typeof config.probeOnlyOnBanned === 'boolean') options.probeOnlyOnBanned = config.probeOnlyOnBanned;
    if (typeof config.noProbing === 'boolean') options.noProbing = config.noProbing;
    if (typeof config.probingPolicy === 'string' && PROBING_POLICIES.includes(config.probingPolicy)) {
        options.probingPolicy = config.probingPolicy as CdnBalancerProbingOptions['probingPolicy'];
    }
    if (typeof config.chunkExpirationProbingMs === 'number' && config.chunkExpirationProbingMs >= 0) {
        options.chunkExpirationProbingMs = config.chunkExpirationProbingMs;
    }
    if (typeof config.timeBetweenHeads === 'number' && config.timeBetweenHeads >= 0) {
        options.timeBetweenHeads = config.timeBetweenHeads;
    }
    return options;
};
